import React, { lazy, Suspense, useEffect, useState } from "react";
import "react-bootstrap-table-next/dist/react-bootstrap-table2.min.css";
import "react-bootstrap-table2-toolkit/dist/react-bootstrap-table2-toolkit.min.css";
import { IoMdEye } from "react-icons/io";
import { MdCancel } from "react-icons/md";
import { SubTitle } from "../../Components/CustomComponents/index.js";
import SimpleTable from "../../Components/Table/SimpleTable.js";
import Toast, { SuccessToast } from "../../Hooks/useToast.js";
import styles from "./styles/delayed_orders.module.scss";
import moment from "moment/moment.js";
import {
  Badge,
  Col,
  OverlayTrigger,
  Row,
  Spinner,
  Tooltip,
} from "react-bootstrap";
import NameImageComponent from "../../Components/CustomComponents/NameImageComponent.js";
import { OrderStatus } from "../../constants.js";
import {
  useGetDelayedOrdersQuery,
  useRejectOrderMutation,
} from "../../Data/services/ordersApi.js";
import CustomLoader from "../../Components/CustomComponents/CustomLoader.js";

const OrderDetailModal = lazy(() =>
  import("../../Components/Modals/OrderDetailModal.js")
);
const RejectOrderModal = lazy(() =>
  import("../../Components/Modals/RejectOrderModal.js")
);

const DelayedOrdersScreen = () => {
  const [indicationMessage, setIndicationMessage] = useState("loading...");
  const [rowData, setRowData] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);

  const [loading, setLoading] = useState(false);
  const [detailModal, setDetailModal] = useState(false);
  const [rejectModal, setRejectModal] = useState(false);

  const { data, isSuccess, isLoading, isFetching, refetch } =
    useGetDelayedOrdersQuery(undefined, {
      refetchOnMountOrArgChange: true,
    });

  const [rejectOrder, { isLoading: isRejecting }] = useRejectOrderMutation();

  useEffect(() => {
    if (data && data.data) {
      console.log(data.data,"data.data")
      setRowData(data.data);
      if (data.data.length === 0) {
        setIndicationMessage("No delayed orders found");
      }
    }
  }, [data]);
  const handleDetailModalOpen = (order) => {
    setSelectedOrder(order);
    setDetailModal(true);
  };

  const handleDetailModalClose = () => {
    setDetailModal(false);
    setSelectedOrder(null);
  };

  const handleRejectModalOpen = (order) => {
    setSelectedOrder(order);
    setRejectModal(true);
  };

  const handleRejectModalClose = () => {
    setRejectModal(false);
    setSelectedOrder(null);
  };

  const handleRejectOrder = async (orderSlug, rejectionReason) => {
    try {
      setLoading(true);
      const response = await rejectOrder({
        slug: orderSlug,
        rejection_reason: rejectionReason,
      });

      if (response.error) {
        Toast(
          response.error?.data?.message || "Failed to reject order",
          "error"
        );
      } else if (response.data && response.data.code === 200) {
        SuccessToast(response.data.message || "Order rejected successfully");
        handleRejectModalClose();
        refetch();
      }
    } catch (error) {
      Toast("An error occurred while rejecting the order", "error");
    } finally {
      setLoading(false);
    }
  };

  function indication() {
    return indicationMessage;
  }

  const columns = [
    {
      dataField: "order_id",
      text: "Order #",
      sort: true,
      headerStyle: {
        width: "100px",
        minWidth: "100px",
      },
      style: {
        width: "100px",
        minWidth: "100px",
      },
      formatter: (cellContent, row) => {
        return <span className={styles.orderNumber}>{row.order_id}</span>;
      },
    },
    {
      dataField: "user",
      text: "Customer",
      sort: true,
      headerStyle: {
        width: "150px",
        minWidth: "150px",
      },
      style: {
        width: "150px",
        minWidth: "150px",
      },
      formatter: (cellContent, row) => {
        return (
          <div className={styles.userCell}>
            <NameImageComponent
              name={row.user?.name || row.user?.firstname}
              image={row.user?.image_url}
            />
          </div>
        );
      },
    },
    {
      dataField: "restaurant",
      text: "Restaurant",
      sort: true,
      headerStyle: {
        width: "180px",
        minWidth: "180px",
      },
      style: {
        width: "180px",
        minWidth: "180px",
      },
      formatter: (cellContent, row) => {
        return (
          <div className={styles.restaurantCell}>
            <NameImageComponent
              name={row.restaurant?.name || row.restaurant?.firstname || ""}
              image={row.restaurant?.image_url}
            />
          </div>
        );
      },
    },
    {
      dataField: "order_items",
      text: "Items",
      headerAlign: "center",
      align: "center",
      headerStyle: {
        width: "80px",
        minWidth: "80px",
      },
      style: {
        width: "80px",
        minWidth: "80px",
      },
      formatter: (cellContent, row) => {
        return (
          <span className={styles.itemCount}>{row.order_items?.length}</span>
        );
      },
    },
    {
      dataField: "total_amount",
      text: "Total Amount",
      headerAlign: "center",
      align: "center",
      headerStyle: {
        width: "120px",
        minWidth: "120px",
      },
      style: {
        width: "120px",
        minWidth: "120px",
      },
      formatter: (cellContent, row) => {
        return (
          <span className={`${styles.amount} ${styles.totalAmount}`}>
            GHS:{(row.total_amount).toFixed(2)}
          </span>
        );
      },
    },
    {
      dataField: "delay_minutes",
      text: "Delay",
      headerAlign: "center",
      align: "center",
      sort: true,
      headerStyle: {
        width: "100px",
        minWidth: "100px",
      },
      style: {
        width: "100px",
        minWidth: "100px",
      },
      formatter: (cellContent, row) => {
        return (
          <Badge pill bg="danger" className={styles.delayBadge}>
            {row.delay_minutes} min
          </Badge>
        );
      },
    },
    {
      dataField: "createdAt",
      text: "Order Date",
      sort: true,
      headerAlign: "center",
      align: "center",
      headerStyle: {
        width: "120px",
        minWidth: "120px",
      },
      style: {
        width: "120px",
        minWidth: "120px",
      },
      formatter: (cellContent, row) => {
        const agoFormat = moment(row.createdAt).fromNow();
        return (
          <OverlayTrigger
            placement="top"
            delay={{ show: 250, hide: 400 }}
            overlay={renderTooltip(row.createdAt)}
          >
            <span className={styles.dateCell}>{agoFormat}</span>
          </OverlayTrigger>
        );
      },
    },
    {
      dataField: "OS",
      text: "Status",
      headerAlign: "center",
      align: "center",
      headerStyle: {
        width: "140px",
        minWidth: "140px",
      },
      style: {
        width: "140px",
        minWidth: "140px",
      },
      formatter: (cellContent, row) => {
        return (
          <Badge pill bg="warning" className={styles.statusBadge}>
            {row.OS?.order_title || "Delayed"}
          </Badge>
        );
      },
    },
    {
      dataField: "actions",
      text: "Actions",
      headerAlign: "center",
      align: "center",
      headerStyle: {
        width: "180px",
        minWidth: "180px",
      },
      style: {
        width: "180px",
        minWidth: "180px",
      },
      formatter: (cellContent, row) => {
        return (
          <div
            className={`d-flex justify-content-center align-items-center ${styles.mobileActions}`}
          >
            <div
              className={`d-flex justify-content-center align-items-center bg-primary-hover rounded-3 p-2 mx-1 actionButton`}
              role="button"
              onClick={() => handleDetailModalOpen(row)}
              title="View Details"
            >
              <span className={`d-flex align-items-center pe-1`}>
                <IoMdEye />
              </span>
              <span className="d-none d-xl-block">View</span>
            </div>
            <div
              className={`d-flex justify-content-center align-items-center bg-danger-hover rounded-3 p-2 mx-1 actionButton text-danger`}
              role="button"
              onClick={() => handleRejectModalOpen(row)}
              title="Reject Order"
            >
              <span className={`d-flex align-items-center pe-1`}>
                <MdCancel />
              </span>
              <span className="d-none d-xl-block">Reject</span>
            </div>
          </div>
        );
      },
    },
  ];

  return (
    <div className={styles.MainContainer}>
      {detailModal && selectedOrder && (
        <Suspense fallback={<Spinner animation="grow" size="sm" />}>
          <OrderDetailModal
            handleModalClose={handleDetailModalClose}
            rowData={selectedOrder}
            loading={loading}
          />
        </Suspense>
      )}

      {rejectModal && selectedOrder && (
        <Suspense fallback={<Spinner animation="grow" size="sm" />}>
          <RejectOrderModal
            handleModalClose={handleRejectModalClose}
            orderData={selectedOrder}
            onReject={handleRejectOrder}
            loading={loading || isRejecting}
          />
        </Suspense>
      )}

      <div className={styles.headerContainer}>
        <Row className="w-100 align-items-center">
          <Col xs={12} md={8} className="mb-3 mb-md-0">
            <div>
              <SubTitle text={"Delayed Orders"} classes="text-black" />
              <p className="text-muted mb-0">
                Orders that have exceeded the wait time threshold
              </p>
            </div>
          </Col>
          <Col xs={12} md={4} className="text-end">
            <div className={styles.statsCard}>
              <div className={styles.statItem}>
                <span className={styles.statLabel}>Total Delayed:</span>
                <span className={styles.statValue}>{rowData.length}</span>
              </div>
            </div>
          </Col>
        </Row>
      </div>

      {!isSuccess || isLoading ? (
        <CustomLoader />
      ) : (
        <div className={styles.mainTable}>
          <div className={styles.tableContent}>
            <SimpleTable
              data={rowData}
              columns={columns}
              indication={indication}
              showPagination={false}
            />
          </div>
        </div>
      )}
    </div>
  );
};

const renderTooltip = (date) => (
  <Tooltip id="button-tooltip">
    {moment(date).format("YYYY-MM-DD HH:mm:ss")}
  </Tooltip>
);

export default DelayedOrdersScreen;
